const rgbConversion = (r) => {
    /* istanbul ignore next */
    if ((r /= 255) <= 0.04045)
        return r / 12.92;
    return Math.pow((r + 0.055) / 1.055, 2.4);
};
const xyzConversion = (t) => {
    if (t > 0.008856452)
        return Math.pow(t, 1 / 3);
    return 7.787 * t + 16 / 116;
};
export const opacityConversion = (a) => {
    // Opacity value should be between 0-1
    if (a > 1) {
        return 1;
    }
    else if (a < 0) {
        return 0;
    }
    return a;
};
/**
 * Convert RGB to XYZ
 * @example
 * import { convertRGBToXYZ } from '@design-stack-vista/i18n-color-name';
 *
 * const [x, y, z] = convertRGBToXYZ(0.010567223840837078, 0.009817732456850208, 0.009442832760537707);
 * // [8.838672818820775, 4.624125067401535, 1.53385385185984]
 * @param {number} r Red value from 0 to 255
 * @param {number} g Green value from 0 to 255
 * @param {number} b Blue value from 0 to 255
 * @param {number} a Opacity value from 0 to 1 with a default value of 1 if not sent
 * @return {array}
 */
export const convertRGBToXYZ = (r, g, b, a = 1) => {
    r = rgbConversion(r);
    g = rgbConversion(g);
    b = rgbConversion(b);
    a = opacityConversion(a);
    const x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375;
    const y = r * 0.2126729 + g * 0.7151522 + b * 0.072175;
    const z = r * 0.0193339 + g * 0.119192 + b * 0.9503041;
    return [x, y, z];
};
/**
 * Convert XYZ to LAB
 * @example
 * import { convertXYZToLAB } from '@design-stack-vista/i18n-color-name';
 *
 * const [l, a, b] = convertXYZToLAB(0, 0, 0);
 * @param {number} x Value
 * @param {number} y Value
 * @param {number} z Value
 */
export const convertXYZToLAB = (x, y, z) => {
    // using 10o Observer (CIE 1964)
    // CIE10_D65 = {94.811f, 100f, 107.304f} => Daylight
    const referenceX = 0.94811;
    const referenceY = 1;
    const referenceZ = 1.07304;
    x = x / referenceX;
    y = y / referenceY;
    z = z / referenceZ;
    x = xyzConversion(x);
    y = xyzConversion(y);
    z = xyzConversion(z);
    const l = 116 * y - 16;
    const a = 500 * (x - y);
    const b = 200 * (y - z);
    return [l, a, b];
};
/**
 * Compare distance between two points, it uses Euclidean distance formula.
 * It will calculate distance between 2 LAB coordinates
 * @example
 * import { distance } from '@design-stack-vista/i18n-color-name';
 *
 * const dist = distance([0, 0, 0], [8.838672818820775, 4.624125067401535, 1.53385385185984]);
 * // 10.092441601317182
 * @param {array} l1 LAB coordinate of first color
 * @param {array} l2 LAB coordinate of second color
 * @returns {number}
 */
export const distance = (l1, l2) => {
    // Delta E (CIE 1976)
    // see http://www.brucelindbloom.com/index.html?Equations.html
    let sumSq = 0;
    for (let i = 0; i < l1.length; i++) {
        const d = (l1[i] || 0) - (l2[i] || 0);
        sumSq += d * d;
    }
    return Math.sqrt(sumSq);
};
//# sourceMappingURL=lab.js.map